import { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Copy, Crown, Users, Play, LogOut, Share2, Link as LinkIcon, MessageSquare } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { useGameStore } from '@/lib/gameStore';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { mapDatabaseError } from '@/lib/errorUtils';
import PlayerAvatar from './PlayerAvatar';
import confetti from 'canvas-confetti';

type MusicIntensity = 'calm' | 'waiting' | 'medium' | 'intense' | 'critical' | 'victory' | 'defeat';

interface LobbyProps {
  onStartGame: () => void;
  onLeave: () => void;
  setMusicIntensity: (intensity: MusicIntensity) => void;
}

export default function Lobby({ onStartGame, onLeave, setMusicIntensity }: LobbyProps) {
  const { currentRoom, playerId, updateRoom } = useGameStore();
  const [showQR, setShowQR] = useState(false);

  const gameLink = `https://sixsevengame.ai/join/${currentRoom?.id || ''}`;
  const { toast } = useToast();
  const [copying, setCopying] = useState(false);

  const isHost = currentRoom?.hostId === playerId;
  const canStart = (currentRoom?.players.length || 0) >= 2;

  // Set waiting music intensity when in lobby
  useEffect(() => {
    setMusicIntensity('waiting');
  }, [setMusicIntensity]);

  // Track previous host ID to detect host changes
  const previousHostIdRef = useRef<string | null>(currentRoom?.hostId || null);

  useEffect(() => {
    if (!currentRoom) return;

    // Subscribe to player changes
    const playerChannel = supabase
      .channel(`lobby-players:${currentRoom.id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'players',
          filter: `room_id=eq.${currentRoom.id}`
        },
        async (payload) => {
          // Fetch updated players
          const { data: players } = await supabase
            .from('players')
            .select('*')
            .eq('room_id', currentRoom.id)
            .order('joined_at', { ascending: true });

          if (players && players.length > 0) {
            // Check if the current host is still in the room
            const currentHostStillExists = players.some(p => p.id === currentRoom.hostId);
            
            if (!currentHostStillExists) {
              // Host left! Auto-promote the oldest player (first in list)
              const newHost = players[0];
              
              // Update the player to be the new host
              await supabase
                .from('players')
                .update({ is_host: true })
                .eq('id', newHost.id);
              
              // Update the room's host_id
              const { data: { session } } = await supabase.auth.getSession();
              
              // Only the new host can update the room (they need to have the auth)
              if (newHost.auth_user_id === session?.user?.id) {
                await supabase
                  .from('rooms')
                  .update({ 
                    host_id: newHost.id,
                    auth_user_id: session.user.id 
                  })
                  .eq('id', currentRoom.id);
              }
              
              // Update local state
              updateRoom({
                hostId: newHost.id,
                players: players.map(p => ({
                  id: p.id,
                  name: p.name,
                  isHost: p.id === newHost.id,
                  stressLevel: p.stress_level as any,
                  isActive: p.is_active,
                }))
              });
              
              // Show toast and confetti for the new host
              if (newHost.id === playerId) {
                toast({
                  title: "👑 You are now the host!",
                  description: "The previous host left the room",
                });
                confetti({
                  particleCount: 100,
                  spread: 70,
                  origin: { y: 0.6 }
                });
              } else {
                toast({
                  title: `👑 ${newHost.name} is now the host`,
                  description: "The previous host left the room",
                });
              }
            } else {
              // Normal player update
              updateRoom({
                players: players.map(p => ({
                  id: p.id,
                  name: p.name,
                  isHost: p.is_host,
                  stressLevel: p.stress_level as any,
                  isActive: p.is_active,
                }))
              });
            }
          }
        }
      )
      .subscribe();

    // Subscribe to room status changes for guests to auto-navigate
    const roomChannel = supabase
      .channel(`lobby-room:${currentRoom.id}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'rooms',
          filter: `id=eq.${currentRoom.id}`
        },
        (payload) => {
          const newRoom = payload.new as any;
          if (newRoom.status === 'playing') {
            updateRoom({ status: 'playing' });
            onStartGame();
          }
          // Update hostId if it changed from database
          if (newRoom.host_id !== currentRoom.hostId) {
            updateRoom({ hostId: newRoom.host_id });
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(playerChannel);
      supabase.removeChannel(roomChannel);
    };
  }, [currentRoom?.id, currentRoom?.hostId, onStartGame, playerId, toast, updateRoom]);

  const copyRoomCode = async () => {
    if (!currentRoom) return;
    setCopying(true);
    await navigator.clipboard.writeText(currentRoom.id);
    toast({ title: "Room code copied!", description: "Share with your friends" });
    setTimeout(() => setCopying(false), 2000);
  };

  const copyGameLink = async () => {
    await navigator.clipboard.writeText(gameLink);
    toast({ title: "Link copied! 🔗", description: "Share the link with your friends!" });
  };

  const shareViaWhatsApp = () => {
    const message = `Join me for 67 CHAOS! 🎮🔥\nClick here: ${gameLink}\nOr enter code: ${currentRoom?.id}`;
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const shareViaSMS = () => {
    const message = `Join me for 67 CHAOS! 🎮 ${gameLink}`;
    window.location.href = `sms:?body=${encodeURIComponent(message)}`;
  };

  const shareNative = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: '67 CHAOS - Join my game!',
          text: `Join me for 67 CHAOS! Enter code: ${currentRoom?.id}`,
          url: gameLink,
        });
        toast({ title: 'Shared successfully! 🚀' });
      } catch (err: any) {
        if (err.name !== 'AbortError') {
          // Fallback to copy
          await copyGameLink();
        }
      }
    } else {
      await copyGameLink();
    }
  };

  const handleStartGame = async () => {
    if (!currentRoom || !canStart) return;

    try {
      // Verify auth session is still valid
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast({ 
          title: "Session expired", 
          variant: "destructive", 
          description: "Please rejoin the room" 
        });
        onLeave();
        return;
      }

      const { error } = await supabase
        .from('rooms')
        .update({ status: 'playing', game_start_time: new Date().toISOString() })
        .eq('id', currentRoom.id);

      if (error) {
        console.error('Error starting game:', error);
        toast({ title: "Failed to start game", variant: "destructive", description: mapDatabaseError(error) });
        return;
      }

      // Re-fetch room to verify status changed
      const { data: updatedRoom, error: fetchError } = await supabase
        .from('rooms')
        .select('status')
        .eq('id', currentRoom.id)
        .single();

      if (fetchError || !updatedRoom) {
        toast({ 
          title: "Failed to start game", 
          variant: "destructive", 
          description: "Room verification failed" 
        });
        return;
      }

      if (updatedRoom.status !== 'playing') {
        toast({ 
          title: "Failed to start game", 
          variant: "destructive", 
          description: "Status update verification failed" 
        });
        return;
      }

      // Only proceed if database update succeeded and was verified
      updateRoom({ status: 'playing' });
      onStartGame();
    } catch (error) {
      console.error('Error starting game:', error);
      toast({ title: "Failed to start game", variant: "destructive" });
    }
  };

  const handleLeave = async () => {
    if (!currentRoom || !playerId) return;

    try {
      // Delete player record (RLS policy ensures only owner can delete their own record)
      await supabase
        .from('players')
        .delete()
        .eq('id', playerId);

      // Sign out the anonymous user
      await supabase.auth.signOut();
      
      // Mark tutorial as seen so it doesn't re-show on leave
      localStorage.setItem('67chaos-tutorial-seen', 'true');

      onLeave();
    } catch (error) {
      console.error('Error leaving room:', error);
    }
  };

  if (!currentRoom) return null;

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-4 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-background via-card to-background" />

      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="relative z-10 w-full max-w-2xl space-y-6"
      >
        {/* Room Code Display */}
        <Card className="glass-card p-8 text-center space-y-4">
          <div className="space-y-2">
            <p className="text-sm font-bold uppercase tracking-wide text-muted-foreground">
              Room Code
            </p>
            <h1 className="text-6xl font-black tracking-widest text-primary text-shadow">
              {currentRoom.id}
            </h1>
          </div>

          {/* Sharing Options */}
          <div className="grid grid-cols-2 gap-2">
            <Button
              onClick={copyRoomCode}
              variant="outline"
              className="font-bold"
              size="lg"
            >
              <Copy className="mr-2 h-4 w-4" />
              CODE
            </Button>
            
            <Button
              onClick={copyGameLink}
              variant="outline"
              className="font-bold"
              size="lg"
            >
              <LinkIcon className="mr-2 h-4 w-4" />
              LINK
            </Button>
          </div>

          <div className="grid grid-cols-3 gap-2">
            <Button
              onClick={shareViaWhatsApp}
              className="font-bold bg-[#25D366] hover:bg-[#1DA851]"
              size="lg"
            >
              <Share2 className="mr-1 h-4 w-4" />
              WA
            </Button>

            <Button
              onClick={shareViaSMS}
              className="font-bold"
              variant="outline"
              size="lg"
            >
              <MessageSquare className="mr-1 h-4 w-4" />
              SMS
            </Button>

            <Button
              onClick={shareNative}
              className="font-bold"
              size="lg"
            >
              <Share2 className="mr-1 h-4 w-4" />
              More
            </Button>
          </div>

          <Dialog open={showQR} onOpenChange={setShowQR}>
            <DialogTrigger asChild>
              <Button
                variant="outline"
                className="font-bold w-full"
                size="lg"
              >
                📱 Show QR Code
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="text-2xl font-black text-center">SCAN TO JOIN</DialogTitle>
              </DialogHeader>
              <div className="flex justify-center p-6 bg-white rounded-2xl">
                <QRCodeSVG 
                  value={gameLink} 
                  size={256}
                  level="H"
                  includeMargin
                />
              </div>
              <p className="text-center text-sm text-muted-foreground font-bold">
                Scan with your phone to join!
              </p>
            </DialogContent>
          </Dialog>
        </Card>

        {/* Players List */}
        <Card className="glass-card p-6 space-y-4">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Users className="h-5 w-5" />
            <span className="font-bold uppercase tracking-wide">
              Players ({currentRoom.players.length}/8)
            </span>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {currentRoom.players.map((player, index) => (
              <motion.div
                key={player.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="flex flex-col items-center gap-2"
              >
                <PlayerAvatar player={player} />
                <div className="text-center">
                  <p className="font-semibold text-sm truncate max-w-[100px]">
                    {player.name}
                  </p>
                  {player.isHost && (
                    <div className="flex items-center justify-center gap-1 text-xs text-primary">
                      <Crown className="h-3 w-3" />
                      <span>Host</span>
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </Card>

        {/* Actions */}
        <div className="grid gap-3">
          {isHost && (
            <Button
              onClick={handleStartGame}
              disabled={!canStart}
              className="h-16 text-lg font-bold uppercase tracking-wide glow-green hover:scale-105 transition-transform"
            >
              <Play className="mr-2 h-6 w-6" />
              Start Game {!canStart && "(Need 2+ players)"}
            </Button>
          )}

          <Button
            onClick={handleLeave}
            variant="secondary"
            className="h-12 font-bold"
          >
            <LogOut className="mr-2 h-4 w-4" />
            Leave Room
          </Button>
        </div>

        {/* Waiting message for non-hosts */}
        {!isHost && (
          <motion.p
            animate={{ opacity: [0.5, 1, 0.5] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="text-center text-muted-foreground font-semibold"
          >
            Waiting for host to start the game...
          </motion.p>
        )}
      </motion.div>
    </div>
  );
}
